function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { getFilesFromDragEvent } from "html-dir-content";
import { isEmpty, isFunction } from "@rpldy/shared";
import { useUploadyContext, markAsUploadOptionsComponent } from "@rpldy/shared-ui";
const getShouldHandleDrag = (e, shouldHandle) => isEmpty(shouldHandle) || shouldHandle === true || isFunction(shouldHandle) && shouldHandle(e);
const isOnTarget = (e, containerElm, allowContains) => {
  return e.target === containerElm ||
  // $FlowIssue[incompatible-call] flow needs to figure it out :(
  allowContains && (containerElm === null || containerElm === void 0 ? void 0 : containerElm.contains(e.target));
};
const UploadDropZone = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    className,
    id,
    children,
    onDragOverClassName,
    dropHandler,
    htmlDirContentParams,
    shouldRemoveDragOver,
    shouldHandleDrag,
    enableOnContains = true,
    extraProps,
    ...uploadOptions
  } = _ref;
  const {
    upload
  } = useUploadyContext();
  const containerRef = useRef(null);
  const dragLeaveTrackerRef = useRef(false);
  useImperativeHandle(ref, () => containerRef.current, []);

  //using ref so upload can stay memoized
  const uploadOptionsRef = useRef();
  uploadOptionsRef.current = uploadOptions;
  const handleEnd = useCallback(() => {
    dragLeaveTrackerRef.current = false;
    if (containerRef.current && onDragOverClassName) {
      containerRef.current.classList.remove(onDragOverClassName);
    }
  }, [onDragOverClassName, containerRef]);
  const dropFileHandler = useCallback(e => {
    const getFiles = () => getFilesFromDragEvent(e, htmlDirContentParams || {});
    return dropHandler ? Promise.resolve(dropHandler(e, getFiles)) : getFiles();
  }, [dropHandler, htmlDirContentParams]);
  const handleDropUpload = useCallback(e => {
    dropFileHandler(e).then(files => {
      upload(files, uploadOptionsRef.current);
    });
  }, [upload, dropFileHandler, uploadOptionsRef]);
  const onDragEnter = useCallback(e => {
    const isHandling = getShouldHandleDrag(e, shouldHandleDrag) && isOnTarget(e, containerRef.current, enableOnContains);
    if (isHandling) {
      dragLeaveTrackerRef.current = true;
      if (containerRef.current && onDragOverClassName) {
        containerRef.current.classList.add(onDragOverClassName);
      }
    }
  }, [onDragOverClassName, containerRef, shouldHandleDrag, enableOnContains]);
  const onDragOver = useCallback(e => {
    if (dragLeaveTrackerRef.current) {
      //must have drag over event handler with preventDefault for drop to work
      e.preventDefault();
    }
  }, []);
  const onDrop = useCallback(e => {
    if (dragLeaveTrackerRef.current) {
      e.preventDefault();
      e.persist();
      handleEnd();
      handleDropUpload(e);
    }
  }, [handleEnd, handleDropUpload]);
  const onDragLeave = useCallback(e => {
    if (dragLeaveTrackerRef.current && e.target === containerRef.current || shouldRemoveDragOver !== null && shouldRemoveDragOver !== void 0 && shouldRemoveDragOver(e)) {
      handleEnd();
    }
  }, [handleEnd, containerRef, shouldRemoveDragOver]);
  const onDragEnd = useCallback(e => {
    if (dragLeaveTrackerRef.current) {
      e.preventDefault();
      e.stopPropagation();
      handleEnd();
    }
  }, [handleEnd]);
  return /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    className: className,
    ref: containerRef,
    onDragOver: onDragOver,
    onDragEnter: onDragEnter,
    onDrop: onDrop,
    onDragLeave: onDragLeave,
    onDragEnd: onDragEnd
  }, extraProps), children);
});
markAsUploadOptionsComponent(UploadDropZone);
export default UploadDropZone;